import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { membersAPI, settingsAPI, paymentsAPI } from '../../utils/api';
import { todayInputDate, calcExpiryDate, formatCurrency } from '../../utils/helpers';
import toast from 'react-hot-toast';

const PLAN_MONTHS = { Monthly: 1, Quarterly: 3, Yearly: 12 };

const defaultForm = {
  name: '', phone: '', age: '', gender: 'Male',
  plan: 'Monthly', shift: 'Morning',
  joiningDate: todayInputDate(),
  emergencyName: '', emergencyPhone: '',
  healthNotes: '',
  // Payment
  paymentMode: 'Cash',
  discount: '0',
  markAsPaid: true,
};

function PaymentBreakdown({ pricing, plan, discount }) {
  if (!pricing) return null;
  const months = PLAN_MONTHS[plan] || 1;
  const planFee = pricing.monthlyFee * months;
  const disc = parseFloat(discount) || 0;
  const payable = Math.max(0, pricing.admissionFee + planFee - disc);
  const showDiscount = plan !== 'Monthly';

  return (
    <div className="mt-4 rounded-xl overflow-hidden border border-blue-100">
      <div className="bg-blue-600 text-white px-4 py-2.5">
        <p className="text-sm font-semibold">First Payment Breakdown</p>
        <p className="text-xs text-blue-200 mt-0.5">One-time admission + {plan} plan</p>
      </div>
      <div className="bg-blue-50 px-4 py-3 space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Admission Fee <span className="text-xs text-gray-400">(one-time)</span></span>
          <span className="font-medium text-gray-800">{formatCurrency(pricing.admissionFee)}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">{plan} Fee
            <span className="text-xs text-gray-400 ml-1">
              ({months} mo × {formatCurrency(pricing.monthlyFee)})
            </span>
          </span>
          <span className="font-medium text-gray-800">{formatCurrency(planFee)}</span>
        </div>
        {showDiscount && disc > 0 && (
          <div className="flex justify-between text-sm text-green-600">
            <span>Discount</span>
            <span>- {formatCurrency(disc)}</span>
          </div>
        )}
        <div className="border-t border-blue-200 pt-2 flex justify-between">
          <span className="font-bold text-gray-800">Total Payable Today</span>
          <span className="font-bold text-xl text-blue-600">{formatCurrency(payable)}</span>
        </div>
      </div>
      <div className="bg-white px-4 py-2 border-t border-blue-100">
        <p className="text-xs text-gray-500">
          🔄 From next {plan === 'Monthly' ? 'month' : plan === 'Quarterly' ? 'quarter' : 'year'}: {formatCurrency(planFee)} only (no admission fee)
        </p>
      </div>
    </div>
  );
}

function AddMember() {
  const [form, setForm] = useState(defaultForm);
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    settingsAPI.getPricing().then(res => setPricing(res.data)).catch(() => {});
  }, []);

  const expiryDate = form.joiningDate
    ? calcExpiryDate(form.joiningDate, form.plan).toISOString().split('T')[0]
    : '';

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handlePhoto = e => {
    const file = e.target.files[0];
    if (file) { setPhoto(file); setPhotoPreview(URL.createObjectURL(file)); }
  };

  const getPayable = () => {
    if (!pricing) return 0;
    const months = PLAN_MONTHS[form.plan] || 1;
    const planFee = pricing.monthlyFee * months;
    const disc = parseFloat(form.discount) || 0;
    return Math.max(0, pricing.admissionFee + planFee - disc);
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.age) { toast.error('Please fill all required fields'); return; }
    if (!/^\d{10}$/.test(form.phone)) { toast.error('Phone number must be exactly 10 digits'); return; }
    setLoading(true);
    try {
      // Create member
      const formData = new FormData();
      Object.entries(form).forEach(([k, v]) => {
        if (!['emergencyName', 'emergencyPhone', 'paymentMode', 'discount', 'markAsPaid'].includes(k))
          formData.append(k, v);
      });
      formData.append('emergencyContact', JSON.stringify({ name: form.emergencyName, phone: form.emergencyPhone }));
      if (photo) formData.append('photo', photo);

      const memberRes = await membersAPI.create(formData);
      const memberId = memberRes.data._id;

      // Record first payment if marked
      if (form.markAsPaid && pricing) {
        const payable = getPayable();
        await paymentsAPI.quickPay({
          memberId,
          paymentMode: form.paymentMode,
          discount: parseFloat(form.discount) || 0,
        });
      }

      toast.success('Member added successfully!');
      navigate('/members');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add member');
    } finally {
      setLoading(false);
    }
  };

  const showDiscount = form.plan !== 'Monthly';

  return (
    <div className="max-w-3xl mx-auto space-y-5">
      <div className="flex items-center gap-4">
        <button onClick={() => navigate('/members')} className="p-2 hover:bg-gray-100 rounded-lg text-gray-600">← Back</button>
        <h2 className="text-2xl font-bold text-gray-800">Add New Member</h2>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Photo */}
        <div className="card">
          <h3 className="font-semibold text-gray-700 mb-4">Profile Photo</h3>
          <div className="flex items-center gap-6">
            <div className="w-24 h-24 rounded-xl bg-gray-100 flex items-center justify-center overflow-hidden border-2 border-dashed border-gray-300">
              {photoPreview
                ? <img src={photoPreview} alt="Preview" className="w-full h-full object-cover" />
                : <span className="text-3xl">📷</span>}
            </div>
            <div>
              <label className="btn-secondary cursor-pointer text-sm">
                Choose Photo
                <input type="file" accept="image/*" className="hidden" onChange={handlePhoto} />
              </label>
              <p className="text-xs text-gray-400 mt-2">JPG, PNG or WebP. Max 5MB</p>
            </div>
          </div>
        </div>

        {/* Personal Info */}
        <div className="card">
          <h3 className="font-semibold text-gray-700 mb-4">Personal Information</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label">Full Name *</label>
              <input className="input" name="name" value={form.name} onChange={handleChange} placeholder="Enter full name" required />
            </div>
            <div>
              <label className="label">Phone Number *</label>
              <input className="input" name="phone" value={form.phone} onChange={handleChange} placeholder="10-digit phone" required />
            </div>
            <div>
              <label className="label">Age *</label>
              <input className="input" type="number" name="age" value={form.age} onChange={handleChange} placeholder="Age" min="5" max="100" required />
            </div>
            <div>
              <label className="label">Gender</label>
              <select className="input" name="gender" value={form.gender} onChange={handleChange}>
                <option>Male</option>
                <option>Female</option>
                <option>Other</option>
              </select>
            </div>
          </div>
        </div>

        {/* Membership */}
        <div className="card">
          <h3 className="font-semibold text-gray-700 mb-4">Membership Details</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label">Plan</label>
              <select className="input" name="plan" value={form.plan} onChange={handleChange}>
                <option>Monthly</option>
                <option>Quarterly</option>
                <option>Yearly</option>
              </select>
            </div>
            <div>
              <label className="label">Shift</label>
              <select className="input" name="shift" value={form.shift} onChange={handleChange}>
                <option>Morning</option>
                <option>Evening</option>
                <option>Both</option>
              </select>
            </div>
            <div>
              <label className="label">Joining Date</label>
              <input className="input" type="date" name="joiningDate" value={form.joiningDate} onChange={handleChange} />
            </div>
            <div>
              <label className="label">Expiry Date (auto-calculated)</label>
              <input className="input bg-gray-50 text-gray-500" type="date" value={expiryDate} readOnly />
            </div>
          </div>
        </div>

        {/* ── PAYMENT SECTION ── */}
        <div className="card border-2 border-green-100">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-green-100 rounded-xl flex items-center justify-center text-lg">💳</div>
              <div>
                <h3 className="font-semibold text-gray-800">First Payment</h3>
                <p className="text-xs text-gray-500">Auto-calculated based on plan and pricing</p>
              </div>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <div
                onClick={() => setForm(f => ({ ...f, markAsPaid: !f.markAsPaid }))}
                className={`w-11 h-6 rounded-full transition-colors relative ${form.markAsPaid ? 'bg-green-500' : 'bg-gray-300'}`}
              >
                <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${form.markAsPaid ? 'left-5' : 'left-0.5'}`} />
              </div>
              <span className="text-sm font-medium text-gray-700">Mark as Paid</span>
            </label>
          </div>

          {/* Pricing breakdown */}
          <PaymentBreakdown pricing={pricing} plan={form.plan} discount={form.discount} />

          {form.markAsPaid && (
            <div className="mt-4 space-y-4">
              {/* Discount — only for Quarterly / Yearly */}
              {showDiscount && (
                <div>
                  <label className="label">
                    Discount Amount (₹)
                    <span className="ml-1 text-xs font-normal text-gray-400">— optional, for {form.plan} plan</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 font-bold text-sm">₹</span>
                    <input
                      className="input pl-7"
                      type="number"
                      min="0"
                      name="discount"
                      value={form.discount}
                      onChange={handleChange}
                      placeholder="0"
                    />
                  </div>
                  {parseFloat(form.discount) > 0 && (
                    <p className="text-xs text-green-600 mt-1 font-medium">
                      ✓ Discount of {formatCurrency(parseFloat(form.discount))} applied → Payable: {formatCurrency(getPayable())}
                    </p>
                  )}
                </div>
              )}

              <div>
                <label className="label">Payment Mode</label>
                <div className="flex gap-2">
                  {['Cash', 'UPI', 'Online'].map(mode => (
                    <button
                      key={mode}
                      type="button"
                      onClick={() => setForm(f => ({ ...f, paymentMode: mode }))}
                      className={`flex-1 py-2.5 rounded-xl text-sm font-medium border-2 transition-all ${form.paymentMode === mode ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}
                    >
                      {mode === 'Cash' ? '💵' : mode === 'UPI' ? '📱' : '🏦'} {mode}
                    </button>
                  ))}
                </div>
              </div>

              {/* Final payable amount — prominent */}
              {pricing && (
                <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-xl px-5 py-4">
                  <div>
                    <p className="text-sm font-medium text-gray-700">Amount to Collect Now</p>
                    <p className="text-xs text-gray-400 mt-0.5">via {form.paymentMode}</p>
                  </div>
                  <p className="text-3xl font-bold text-green-600">{formatCurrency(getPayable())}</p>
                </div>
              )}
            </div>
          )}

          {!form.markAsPaid && (
            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-xl text-sm text-yellow-700">
              ⚠️ Payment not marked. You can record it later from the Payments section.
            </div>
          )}
        </div>

        {/* Emergency + Health */}
        <div className="card">
          <h3 className="font-semibold text-gray-700 mb-4">Emergency Contact</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label">Contact Name</label>
              <input className="input" name="emergencyName" value={form.emergencyName} onChange={handleChange} placeholder="Name" />
            </div>
            <div>
              <label className="label">Contact Phone</label>
              <input className="input" name="emergencyPhone" value={form.emergencyPhone} onChange={handleChange} placeholder="Phone" />
            </div>
          </div>
        </div>

        <div className="card">
          <h3 className="font-semibold text-gray-700 mb-4">Health Notes</h3>
          <textarea className="input resize-none" name="healthNotes" rows={3} value={form.healthNotes} onChange={handleChange} placeholder="Any health issues or medical notes (optional)" />
        </div>

        <div className="flex gap-3 justify-end">
          <button type="button" onClick={() => navigate('/members')} className="btn-secondary">Cancel</button>
          <button type="submit" disabled={loading} className="btn-primary min-w-[140px]">
            {loading ? 'Saving...' : `Add Member${form.markAsPaid ? ' & Record Payment' : ''}`}
          </button>
        </div>
      </form>
    </div>
  );
}

export default AddMember;
